"use client";

import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, HeroIcon } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import Image from "next/image";
import { ReactNode, useMemo } from "react";
import { IconType } from "react-icons";

interface PlatformListItemProps {
    onClick?: () => void;
    thumbnail?: {
        src?: string;
    };
    thumbnailBadge?: {
        text: string | ReactNode;
    };
    text: {
        title: string;
        subtitle: string;
        icon?: LucideIcon | HeroIcon | IconType;
    };
    options?: ReactNode;
    selected?: boolean;
}

export default function PlatformListItem({
    onClick,
    thumbnail,
    thumbnailBadge,
    text,
    options,
    selected,
}: PlatformListItemProps) {
    const isMobile = useIsMobile();

    const { thumbnailWidth, titleMinWidth, optionsWidth } = useMemo(
        () => ({
            thumbnailWidth: isMobile ? 150 : 180, // px
            titleMinWidth: isMobile ? 140 : 200, // px
            optionsWidth: 50, // px
        }),
        [isMobile]
    );

    const gridCols = thumbnail
        ? `${thumbnailWidth}px 1fr ${optionsWidth}px`
        : `1fr ${optionsWidth}px`;

    return (
        <div
            onClick={onClick}
            className={cn(
                "group cursor-pointer bg-card hover:bg-accent transition",
                "grid items-center w-full p-4 gap-2 sm:gap-4 overflow-x-auto",
                selected && "border-s-4 border-s-primary"
            )}
            style={{ gridTemplateColumns: gridCols }}
        >
            {thumbnail && (
                <div
                    className="aspect-video rounded-md relative flex-shrink-0 border"
                    style={{
                        width: thumbnailWidth,
                    }}
                >
                    <Image
                        fill
                        src={thumbnail.src || "/images/image-placeholder.png"}
                        alt="Thumbnail"
                        className="rounded-md object-contain"
                        onError={(e) => {
                            console.log("Image load error for:", thumbnail.src);
                            e.currentTarget.src =
                                "/images/image-placeholder.png";
                        }}
                    />
                    {thumbnailBadge && (
                        <Badge className="absolute bottom-2 left-2 font-normal px-2">
                            {thumbnailBadge.text}
                        </Badge>
                    )}
                </div>
            )}

            <div
                className="flex items-center gap-3"
                style={{
                    minWidth: titleMinWidth,
                }}
            >
                {text.icon && <text.icon className="size-5 flex-shrink-0" />}
                <div className="flex flex-col gap-0.5 truncate">
                    <h3
                        className="text-base font-medium truncate"
                        title={text.title}
                    >
                        {text.title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                        {text.subtitle}
                    </p>
                </div>
            </div>

            <div className="justify-self-end flex-shrink-0">{options}</div>
        </div>
    );
}
